import { ConnectedPromotionBanner, usePromotion } from '@barkinglabs/promotions-react';
import { PromotionConfigurationKind } from '@barkinglabs/promotions-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useLocale } from '../contexts/LocaleContext';
import { useShowPromotions } from '../contexts/ShowPromotionsContext';
import analytics from '../lib/analytics';
import { getFiGrowthBook, PromotionBannerVariant, useIsGrowthBookEnabled } from '../lib/growthbook';
import { applyCouponToCart } from '../lib/promoCode';
import { resolvePromotionCouponCode } from '../lib/resolvePromotionCouponCode';
import setCouponCookie from '../lib/setCouponCookie';
import { getNanoReferralPetNameCookie } from '../lib/util/nanoReferralPetNameCookie';
import {
  dismissQuizBanner,
  getQuizReferral,
  isQuizBannerDismissed,
  QUIZ_REFERRAL_COUPON_CODE,
} from '../lib/quizReferral';
import { maxTotalDiscountInCents, usePromoCodeDetails } from '../hooks/usePromoCodeDetails';
import QuizPromotionBanner from './QuizPromotionBanner';
import { cartActions } from '../reducers/storeCart';
import { AppState } from '../types';
import CouponBanner from './CouponBanner/CouponBanner';
import getCouponCookie from '../lib/getCouponCookie';
import { NanoBannerV2, showNanoVariant } from './NanoBanner/NanoBanner';
import { webApiUrl } from '../lib/fi-api/apiUtils';
import { useLocation } from 'react-router-dom';

interface ShopPromotionBannerProps {
  showBannerOnApply?: boolean;
  /**
   * Move the ZD chat widget up. Should be specified if we're displaying
   * this promotion as a banner at the bottom of the screen.
   */
  className?: string;
  // Callback function which is called when the banner is shown or hidden.
  bannerShownCallback?: (shown: boolean) => void;
}

const useTrackEvent = () =>
  useCallback((name: string, props?: Record<string, unknown>) => analytics.track(name, props), []);

function useShopPromotionBannerDetails(forcedVariant?: number) {
  const showPromotions = useShowPromotions();
  const { language, country } = useLocale();
  const trackEvent = useTrackEvent();
  const { variant } = usePromotion({
    forcedVariant,
    overrideApiHost: webApiUrl,
    trackEvent,
    country,
  });
  const cart = useSelector((state: AppState) => state.storeShop.cart);
  const showPromoBannerImprovements = useSelector(
    (state: AppState) => !!state.config.siteConfig.enableEcommPromoBannerImprovements,
  );

  const couponCode: string | undefined =
    (variant?.configuration.kind === PromotionConfigurationKind.SIMPLE &&
      resolvePromotionCouponCode(variant.configuration, language, country)) ||
    undefined;

  /**
   * If improved promo banner feature flag is on, show promo code when:
   * 1. Showing promotion is enabled
   * 2. We have a promotion variant
   *
   * If improved promo banner feature flag is off, show promo code when:
   * 1. Showing promotion is enabled
   * 2. The cart doesn't already have a coupon code
   * 3. We have a promotion variant
   */
  let showPromotion = showPromoBannerImprovements
    ? showPromotions && !!variant
    : showPromotions && !cart.couponCode && !!variant;

  // TODO: Add this within promotions-react component
  const isFiPartner = document.cookie.includes('fi_partner');
  showPromotion = showPromotion && !isFiPartner;

  return {
    couponCode,
    showPromotion,
  };
}

function useIsGiftFlow(cartContainsGiftItems?: boolean) {
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const isGiftParam = queryParams.get('isGift') === 'true';
  const isGiftFlow = isGiftParam || cartContainsGiftItems;
  return isGiftFlow;
}

// Auto apply promo code only if isGift flow is detected in URL
function useAutoApplyCouponForGift(onClick: VoidFunction, isGiftFlow?: boolean, couponCode?: string) {
  const [hasApplied, setHasApplied] = useState(false);

  useEffect(() => {
    // From the cart, we want to auto-apply the promo code by simulating a click event on the banner.
    // We wait until couponCode is defined before attempting to apply.
    if (isGiftFlow && couponCode && !hasApplied) {
      onClick();
      setHasApplied(true);
    }
  }, [isGiftFlow, couponCode, hasApplied, onClick]);
}

export default function ShopPromotionBanner({ showBannerOnApply, bannerShownCallback }: ShopPromotionBannerProps) {
  // useGrowthBook returns undefined if we're not wrapped in a GrowthBookProvider. We only use the GrowthBookProvider in
  // the main store flow so in-app webviews won't have this.
  const growthBookEnabled = useIsGrowthBookEnabled();
  const { language, country } = useLocale();
  // Pass the full mixed-case BCP-47 locale (e.g. 'fr-BE', 'de-LU') to
  // ConnectedPromotionBanner. This is the exact key fi-admin writes into the
  // promotion's localizedText entries, so it resolves the correct language
  // variant within multi-locale countries (Belgium, Luxembourg). Lowercasing
  // or truncating to the two-letter code breaks that match.
  const bannerLanguage = language;
  const trackEvent = useTrackEvent();

  const dispatch = useDispatch();
  const cart = useSelector((state: AppState) => state.storeShop.cart);
  const showPromoBannerImprovements = useSelector(
    (state: AppState) => !!state.config.siteConfig.enableEcommPromoBannerImprovements,
  );

  const [error, setError] = useState<string>();
  const [appliedBannerState, setAppliedBannerState] = useState<'never' | 'shown' | 'dismissed'>('never');

  const variantCouponCodeDiscountEnabled = growthBookEnabled
    ? getFiGrowthBook().getFeatureValue<PromotionBannerVariant>(
        'ecom-variant-coupon-code-discount-multivalue',
        PromotionBannerVariant.DEFAULT,
      ) === PromotionBannerVariant.EXPERIMENT
    : false;

  const elevatedNanoOffer = getFiGrowthBook().getFeatureValue<boolean>('nano-elevated-promo-offer', false);

  const promoBannerVariant = variantCouponCodeDiscountEnabled ? 1 : 0;
  const couponCookie = getCouponCookie();
  const { t } = useTranslation();

  let { couponCode, showPromotion } = useShopPromotionBannerDetails(promoBannerVariant);
  const forceNanoBanner = showNanoVariant() || !!getNanoReferralPetNameCookie();

  // Quiz-referred sessions swap the regular promotion banner for the quiz
  // offer ("Up to $X off {petName}'s Fi with code PERFECTFIT") everywhere the
  // promo banner shows — PDP through cart — so the shopper sees one consistent
  // code, not the quiz code on the PDP and a different fi-admin code in the
  // bag. Dismissing it falls back to the regular promotion banner.
  const [quizBannerDismissed, setQuizBannerDismissed] = useState<boolean>(isQuizBannerDismissed);
  const quizReferral = getQuizReferral();
  // The offer amount comes from /code-details per currency (shared promise
  // cache with QuizPromotionBanner, so this costs one request). While it's
  // loading — or when the code has no fixed-amount discount in this currency —
  // the regular promotion banner shows instead.
  const quizCodeDetails = usePromoCodeDetails(quizReferral ? QUIZ_REFERRAL_COUPON_CODE : undefined);
  const quizOfferDiscountCents = quizCodeDetails ? maxTotalDiscountInCents(quizCodeDetails) : 0;
  const showQuizBanner = !!quizReferral && !quizBannerDismissed && !forceNanoBanner && quizOfferDiscountCents > 0;

  showPromotion = showPromotion || forceNanoBanner || showQuizBanner;

  if (forceNanoBanner) {
    couponCode = elevatedNanoOffer ? 'nano99' : 'nano20';
  }

  const doApply = async (theCouponCode: string) => {
    // Don't reapply the coupon code if it's already applied (case-insensitive: Recurly redeems codes
    // case-insensitively, so a case difference must not trigger a redundant apply).
    if (cart.couponCode?.toLowerCase() === theCouponCode?.toLowerCase()) {
      if (appliedBannerState !== 'shown') {
        setAppliedBannerState('shown');
      }

      return true;
    }

    const result = await applyCouponToCart(cart, theCouponCode);
    if (result.kind === 'success') {
      dispatch(cartActions.addCoupon(result.value.code));
      setCouponCookie(result.value.code);
      if (showBannerOnApply) {
        setAppliedBannerState('shown');
      }
      setError(undefined);
      return true;
    } else {
      setError(result.err.message);
      return false;
    }
  };

  useEffect(() => {
    const applyCoupon = async () => {
      // Skip when the partner cookie is set: `useAutoApplyCoupon` already
      // applies partner codes through the Fi-aware path, and re-applying here
      // falls through to Recurly's JS SDK which 404s on partner-scoped codes.
      const isFiPartner = document.cookie.includes('fi_partner');
      if (couponCookie && !isFiPartner) {
        // A cookie code that fails is not swapped for the live promotion's code. Taking that offer
        // means clicking the banner.
        await doApply(couponCookie.code);
      }
    };
    applyCoupon();
    // eslint-disable-next-line
  }, [couponCode]); // Dependency array is non-exhaustive so useEffect runs only on initial page load.

  // Auto-apply promo coupon when referral code is present
  useEffect(() => {
    if (cart.referralCode && couponCode && cart.couponCode?.toLowerCase() !== couponCode.toLowerCase()) {
      doApply(couponCode);
    }
    // eslint-disable-next-line
  }, [cart.referralCode, couponCode, cart.couponCode]);

  const onClick = () => {
    if (couponCode) {
      doApply(couponCode);
    }
  };

  // Auto apply coupon for gift flow but do not render the banner.
  const isGiftFlow = useIsGiftFlow(cart.containsGiftItems);
  useAutoApplyCouponForGift(onClick, isGiftFlow, couponCode);

  // Notify the parent whether the banner is visible. Derived from render
  // conditions below and called via useEffect to avoid setState-during-render.
  const bannerShown = isGiftFlow ? undefined : !cart.referralCode;
  useEffect(() => {
    if (bannerShownCallback && bannerShown !== undefined) {
      bannerShownCallback(bannerShown);
    }
  }, [bannerShownCallback, bannerShown]);

  if (isGiftFlow) {
    return null;
  }

  const promoBannerSupportedCountries = getFiGrowthBook().getFeatureValue<string[]>(
    'ecom-promo-banner-supported-countries',
    ['US', 'CA', 'GB'],
  );
  if (!promoBannerSupportedCountries.includes(country)) {
    return null;
  }

  // If user has entered a referral code, don't render this banner
  // (AppBar component returns a ReferralBanner instead).
  // The promo coupon is auto-applied via useEffect above.
  if (cart.referralCode) {
    return null;
  }

  if (error && appliedBannerState !== 'dismissed') {
    return (
      <CouponBanner
        bannerText={error}
        onDismiss={() => {
          setAppliedBannerState('dismissed');
        }}
        error={true}
      />
    );
  }

  // Quiz-referred sessions get the personalized quiz offer in the banner slot,
  // applying its dedicated coupon. Once applied, confirm with the standard
  // applied banner; dismissing falls back to the regular promotion banner.
  if (showQuizBanner && quizReferral) {
    if (cart.couponCode?.toLowerCase() === QUIZ_REFERRAL_COUPON_CODE.toLowerCase()) {
      return (
        <CouponBanner
          bannerText={t('promo_code_applied_with_code', { couponCode: QUIZ_REFERRAL_COUPON_CODE })}
          onDismiss={() => {
            dismissQuizBanner();
            setQuizBannerDismissed(true);
          }}
        />
      );
    }
    return (
      <QuizPromotionBanner
        referral={quizReferral}
        onClick={() => {
          void doApply(QUIZ_REFERRAL_COUPON_CODE);
        }}
        onDismiss={() => {
          dismissQuizBanner();
          setQuizBannerDismissed(true);
        }}
      />
    );
  }

  // If cart.couponCode (the coupon code in user's cart) doesn't match couponCode (coupon on promotional banner),
  // continue to surface the promotion banner.
  if (showPromotion && (!cart.couponCode || cart.couponCode?.toLowerCase() !== couponCode?.toLowerCase())) {
    if (forceNanoBanner) {
      return <NanoBannerV2 cart={cart} promoCodeApplied={false} onClick={onClick} />;
    }
    return (
      <div className="connectedPromotionBanner">
        <ConnectedPromotionBanner
          apiHost={webApiUrl}
          forceVariant={promoBannerVariant}
          onClick={onClick}
          trackEvent={trackEvent}
          language={bannerLanguage}
          country={country}
        />
      </div>
    );
  }

  if (forceNanoBanner) {
    return <NanoBannerV2 cart={cart} promoCodeApplied={true} onClick={onClick} />;
  }

  return showPromoBannerImprovements ? (
    <div className="connectedPromotionBanner">
      <ConnectedPromotionBanner
        apiHost={webApiUrl}
        forceVariant={promoBannerVariant}
        onClick={onClick}
        trackEvent={trackEvent}
        subtitle={t('promo_code_applied_with_code', { couponCode })}
        language={bannerLanguage}
        country={country}
      />
    </div>
  ) : (
    <CouponBanner
      bannerText={t('promo_applied_exclamation')}
      onDismiss={() => {
        setAppliedBannerState('dismissed');
      }}
    />
  );
}
